/**
 * Part del State necessari per el RefCadAdrWidget
 * Component: RefCadAdrWidgetState.ts
 * Llenguatge: TypeScript
 * Autors: Xavier Naval
 * Versió: 1.0
 * Data: 29/09/2017
 * (c) Ajuntament de Manresa - set 2017 - Departament de Sistemes d'Informació
 */
import { observable, action, computed } from 'mobx';
import RefCadAdrWidgetAPI from '../api/RefCadAdrWidgetAPI';
import { IPropsRefCadAdrWidget } from '../interfaces/IPropsRefCadAdrWidget';
import { IFinca, INumero, IFincaNumero } from '../interfaces/IRefCadAdr';

export default class RefCadAdrWidgetState {
    // Constructor si no li passem cap RefCadAdrWidgetAPI en construeix un per defecte
    constructor(refCadAdrWidgetAPI?: RefCadAdrWidgetAPI, numCod?: number, finCod?: number) {
        this.rcAPI = refCadAdrWidgetAPI || new RefCadAdrWidgetAPI();
        if (numCod) {
            this.setNumeroByNumCod(numCod);
        } else if (finCod) {
            this.setFincaByFinCod(finCod);
        } else {
            this.dataFincaNumero = [];
            this.selFincaNumero = undefined;
        }
    }

    // L'API dels RefCadAdrWidget
    protected rcAPI: RefCadAdrWidgetAPI;

    // Llista de finquesNumeros
    @observable dataFincaNumero: Array<IFincaNumero> = [];

    // La fincaNumero seleccionada
    @observable selFincaNumero?: IFincaNumero;

    // Retorna el NumCod actual
    @computed get NumCod(): number {
        if (this.selFincaNumero) {
            return this.selFincaNumero.NUMCOD;
        }
        return 0;
    }

    // Retorna el FinCod actual
    @computed get FinCod(): number {
        if (this.selFincaNumero) {
            return this.selFincaNumero.FINCOD;
        }
        return 0;
    }

    // Assignem la FincaNumero (normalment ve de la selecció del desplegable)
    @action setFincaNumero(fincaNumero?: IFincaNumero) {
        if (fincaNumero) {
            this.dataFincaNumero = [fincaNumero];
        } else {
            this.dataFincaNumero = [];
        }
        this.selFincaNumero = fincaNumero;
    }

    // Assignem la FincaNumero a partir d'una finca
    private setFinca(finca?: IFinca) {
        if (finca && finca.FINCOD) {
            this.setFincaNumero({
                FINCOD: finca.FINCOD,
                NUMCOD: 0,
                FINREFCAD1: finca.FINREFCAD1,
                FINREFCAD2: finca.FINREFCAD2,
                ADRECA: finca.ADRECA,
                RN_: finca.RN_,
                DESCR: finca.FINREFCAD1 + finca.FINREFCAD2 + ' - ' + finca.ADRECA,
                COORD: []
            });
        } else {
            this.setFincaNumero();
        }
    }

    // Assignem la FincaNumero a partir d'un número
    private setNumero(numero?: INumero) {
        if (numero && numero.NUMCOD) {
            this.setFincaNumero({
                FINCOD: 0,
                NUMCOD: numero.NUMCOD,
                FINREFCAD1: '',
                FINREFCAD2: '',
                ADRECA: numero.ADRECA,
                RN_: numero.RN_,
                DESCR: numero.ADRECA,
                COORD: []
            });
        } else {
            this.setFincaNumero();
        }
    }

    // Llegim la finca per el FinCod
    @action setFincaByFinCod(finCod: number) {
        if (!finCod) {
            this.setFincaNumero();
            return;
        }
        this.setFincaNumero(this.dataFincaNumero.find(item => item.FINCOD === finCod));
        if (!this.selFincaNumero) {
            this.rcAPI.getFincaPerFinCod(finCod).then((result: IFinca) => {
                this.setFinca(result);
            }).catch(() => { /* No fa res*/ });
        }
    }

    // Llegim la finca per la RefCad
    @action setFincaByRefCad(finRefCad1: string, finRefCad2: string) {
        if (!finRefCad1 || !finRefCad2) {
            this.setFincaNumero();
            return;
        }
        this.setFincaNumero(this.dataFincaNumero.find(item => item.FINREFCAD1 === finRefCad1 && item.FINREFCAD2 === finRefCad2));
        if (!this.selFincaNumero) {
            this.rcAPI.getFinquesPerRefCad(finRefCad1 + finRefCad2, 0, 0).then((result: Array<IFinca>) => {
                if (result && result.length === 1) {
                    this.setFinca(result[0]);
                } else {
                    this.setFincaNumero();
                }
            }).catch(() => { /* No fa res*/ });
        }
    }

    // Llegim l'adreça' per el NumCod
    @action setNumeroByNumCod(numCod: number) {
        if (!numCod) {
            this.setFincaNumero();
            return;
        }
        this.setFincaNumero(this.dataFincaNumero.find(item => item.NUMCOD === numCod));
        if (!this.selFincaNumero) {
            this.rcAPI.getNumeroPerNumCod(numCod).then((result: INumero) => {
                this.setNumero(result);
            }).catch(() => { /* No fa res*/ });
        }
    }

    // Intenta deduir si un tetxt és una referència cadastral
    private esReferencia(texte: string): boolean {
        let l = texte.length;
        if (l > 0) {
            if (l > 5) {
                l = 5;
            }
            let i: number;
            for (i = 0; i < l; i++) {
                if (texte[i] < '0' || texte[i] > '9') {
                    return false;
                }
            }
            return true;
        } 
        return false;
    }

    // diu si el paràmetre és polígon parcel·la
    private esPolPar(ref: string): boolean {
        if (ref) {
            ref = ref.trim();
            if (ref && /^(\d+)(\s*[\s,;]\s*)(\d+)?$/.test(ref)) { // Són dos númneros separats per ' ', ',' o ';'
                // Filtre per Manresa. El 1r número entre 1 i 18 el segon entre 1 = 9505 (en el codi posem el plígon entre 1 i 20 i la parcel·la entre 1 i 9999)
                const parts = /^(\d+)(\s*[\s,;]\s*)(\d+)?$/.exec(ref);
                const pol = parseInt(parts[1]);
                if (pol > 0 && pol < 21) {
                    const par = parseInt(parts[3]);
                    // console.log('PolPar: "' + ref + '" -> ' + pol + ' ' + par);
                    return par > 0 && par < 10000;
                }
            }
        }
        return false;
    }

    // Retorna el polígon parcel·la correctament formatat (número de polígon espai número de parcel·la)
    private getPolPar(ref: string): string {
        const parts = /^(\d+)(\s*[\s,;]\s*)(\d+)?$/.exec(ref.trim());
        return parseInt(parts[1]).toString() + ' ' + parseInt(parts[3]).toString();
    }
    
    // Diu si el paràmetre és coordenada
    // Són dos números separats // Són dos númneros separats per ' ', ',' o ';'
    // Si estan separats per ',' el punt decimal pot ser un '.' o una ','
    private esCoord(ref: string): boolean {
        if (ref) {
            ref = ref.trim();
            if (ref && /^(\d+(?:[.,]\d+)?)(\s*[\s,;]\s*)(\d+(?:[.,]\d+)?)?$/.test(ref.trim())) {
                // Filtre per Manresa. X,Y: 397664,39 406392,46 4615771,93 4623596,44
                // Filtre per manresa. Lat,Lon: 41,68816235 41,75824087 1,76926384 1,87473585
                const parts = /^(\d+(?:[.,]\d+)?)(\s*[\s,;]\s*)(\d+(?:[.,]\d+)?)?$/.exec(ref);
                const x = parseFloat(parts[1].replace(',', '.'));
                const y = parseFloat(parts[3].replace(',', '.'));
                // console.log('Coordenada: "' + ref + '" -> ' +  x + ' ' + y);
                return ((x > 390000 && x < 410000 && y > 4610000 && y < 4630000) || 
                        (x > 0 && x < 2 && y > 40 && y < 42) || // Coordenades LonLat WGS84
                        (x > 40 && x < 42 && y > 0 && y < 2)); // Coordenades LatLon WGS84
            }
        }
        return false;
    }
    
    // Retorna la coordenada
    private getCoord(ref: string): Array<number> {
        const parts = /^(\d+(?:[.,]\d+)?)(\s*[\s,;]\s*)(\d+(?:[.,]\d+)?)?$/.exec(ref.trim());
        const x = parseFloat(parts[1].replace(',', '.'));
        const y = parseFloat(parts[3].replace(',', '.'));
        if (x > 390000 && x < 410000 && y > 4610000 && y < 4630000) { // Coordenades Cartesianes X:Y EPSG:25831
            return [x, y];
        } else if (x > 0 && x < 2 && y > 40 && y < 42) { // Coordenades LonLat WGS84
            return [x, y];
        } else { /* if (x > 40 && x < 42 && y > 0 && y < 2) { // Coordenades LatLon WGS84 */
            return [y, x];
        }
    }

    // Posem el input de GENERIC (Per llegir els codis per l'autocomplete de referències cadastrals o adreces)
    @action setInputFINCAADR = (generic: string, props: IPropsRefCadAdrWidget) => {
        // Omplir el autocomplete
        if (generic && generic.length >= (props.minLength || 0) ) {
            if (this.esCoord(generic)) {
                this.dataFincaNumero = [{
                    FINCOD: 0,
                    NUMCOD: 0,
                    FINREFCAD1: '',
                    FINREFCAD2: '',
                    ADRECA: '',
                    RN_: 1,
                    DESCR: generic,
                    COORD: this.getCoord(generic)
                }];
            } else if (this.esPolPar(generic)) {
                this.rcAPI.getFinquesPerPolPar(this.getPolPar(generic), 0, (props.pageSize || 0)).then((result: Array<IFinca>) => {
                    if (result) {
                        this.dataFincaNumero = result.map(item => { return {
                            FINCOD: item.FINCOD,
                            NUMCOD: 0,
                            FINREFCAD1: item.FINREFCAD1,
                            FINREFCAD2: item.FINREFCAD2,
                            ADRECA: item.ADRECA,
                            RN_: item.RN_,
                            DESCR: item.FINREFCAD1 + item.FINREFCAD2 + ' - ' + item.ADRECA,
                            COORD: []
                        }});
                    } else {
                        this.dataFincaNumero = [];
                    }
                }).catch(() => { /* No fa res*/ }); // Si hi ha un error de xarxa, l'ignorem. De vegades és pq s'escriu massa ràpid
            } else if (this.esReferencia(generic)) {
                this.rcAPI.getFinquesPerRefCad(generic, 0, (props.pageSize || 0)).then((result: Array<IFinca>) => {
                    if (result) {
                        this.dataFincaNumero = result.map(item => { return {
                            FINCOD: item.FINCOD,
                            NUMCOD: 0,
                            FINREFCAD1: item.FINREFCAD1,
                            FINREFCAD2: item.FINREFCAD2,
                            ADRECA: item.ADRECA,
                            RN_: item.RN_,
                            DESCR: item.FINREFCAD1 + item.FINREFCAD2 + ' - ' + item.ADRECA,
                            COORD: []
                        }});
                    } else {
                        this.dataFincaNumero = [];
                    }
                }).catch(() => { /* No fa res*/ }); // Si hi ha un error de xarxa, l'ignorem. De vegades és pq s'escriu massa ràpid
            } else {
                this.rcAPI.getNumerosPerAdreca(generic, 0, (props.pageSize || 0)).then((result: Array<INumero>) => {
                    if (result) {
                        this.dataFincaNumero = result.map(item => { return {
                            FINCOD: 0,
                            NUMCOD: item.NUMCOD,
                            FINREFCAD1: '',
                            FINREFCAD2: '',
                            ADRECA: item.ADRECA,
                            RN_: item.RN_,
                            DESCR: item.ADRECA,
                            COORD: []
                        }});
                    } else {
                        this.dataFincaNumero = [];
                    }
                }).catch(() => { /* No fa res*/ }); // Si hi ha un error de xarxa, l'ignorem. De vegades és pq s'escriu massa ràpid
            }
        } else {
            this.dataFincaNumero = [];
        }
    }

}